import { useRef, useState, type PointerEvent, type ReactNode } from 'react'
import type { Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../Dialog/Dialog'
import { Button } from '../Button/Button'
import { Input } from '../Input/Input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Select/Select'
import { ColorPopover, rgba } from './WorkbenchColorPicker'
import { WIDTHS, BitsPopover } from './WorkbenchFloatingBits'
import { colorSlotsFor } from '@/indicators'
import type { IndicatorDef, InputSpec } from '@/indicators'
import { getIndicator } from './types'
import type { IndicatorInstance, IndicatorSettings } from './types'

// Per-indicator settings popup (one screen, no tabs). Reuses the SAME controls
// as the chart/drawing settings: the ColorPopover (swatch grid + opacity) and
// the WIDTHS thickness buttons — nothing here is a new style. Edits apply live
// through onChange; Cancel restores the snapshot taken on mount.

export interface WorkbenchIndicatorSettingsProps {
  chart: Chart
  instance: IndicatorInstance
  /** The def the instance pins (its inputs schema, outputs and TA-Lib reference). */
  def: IndicatorDef
  onChange: (patch: Partial<IndicatorSettings>) => void
  onClose: () => void
}

/** A stored colour can be hex (default) or rgba (after the picker adds opacity).
 * Parse it into the { hex, op } pair the ColorPopover expects. */
function parseColor(c: string | undefined): { hex: string; op: number } {
  if (c && c.startsWith('rgba')) {
    const m = c.match(/[\d.]+/g) ?? []
    const [r = '255', g = '150', b = '0', a = '1'] = m
    const hex =
      '#' + [r, g, b].map((n) => Math.round(Number(n)).toString(16).padStart(2, '0')).join('')
    return { hex, op: Math.round(Number(a) * 100) }
  }
  return { hex: c || '#FF9600', op: 100 }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-body text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

/**
 * A numeric field the user can empty and retype. The text is a local draft; the
 * committed `value` is pushed only when the draft parses to a number the schema
 * accepts (live apply keeps working while typing), and an unusable draft reverts
 * to the committed value on blur. A text input, not type="number": a number
 * input reports '' for partial entries like "0." and would wipe the draft.
 */
function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string
  value: number
  min?: number
  max?: number
  onChange: (v: number) => void
}) {
  const accepts = (raw: string): number | null => {
    if (raw.trim() === '') return null
    const v = Number(raw)
    if (!Number.isFinite(v)) return null
    if (min !== undefined && v < min) return null
    if (max !== undefined && v > max) return null
    return v
  }
  const [draft, setDraft] = useState(String(value))
  // An outside change (Cancel restoring the snapshot, an int rounded on commit)
  // resets the draft; the user's own in-progress text that already reads as the
  // committed value ("50." while typing a float) is left alone.
  const [seen, setSeen] = useState(value)
  if (seen !== value) {
    setSeen(value)
    if (accepts(draft) !== value) setDraft(String(value))
  }
  return (
    <Input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={draft}
      onChange={(e) => {
        const raw = e.target.value
        setDraft(raw)
        const v = accepts(raw)
        if (v !== null) onChange(v)
      }}
      onBlur={() => {
        if (accepts(draft) === null) setDraft(String(value))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
      }}
      className="h-8 w-28 px-2 text-body text-foreground tabular-nums"
    />
  )
}

/** One typed input row: int / float / select / bool, from the def's schema. */
function InputField({ spec, value, onChange }: { spec: InputSpec; value: number; onChange: (v: number) => void }) {
  switch (spec.kind) {
    case 'int':
      return <NumberField label={spec.label} value={value} min={spec.min} max={spec.max} onChange={(v) => onChange(Math.round(v))} />
    case 'float':
      return <NumberField label={spec.label} value={value} min={spec.min} max={spec.max} onChange={onChange} />
    case 'select':
      return (
        <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
          <SelectTrigger size="sm" aria-label={spec.label} className="w-28 text-body">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {spec.options.map((o) => (
              <SelectItem key={o.value} value={String(o.value)} className="text-body">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    case 'bool':
      return (
        <input
          type="checkbox"
          aria-label={spec.label}
          checked={value === 1}
          onChange={(e) => onChange(e.target.checked ? 1 : 0)}
          className="h-4 w-4 accent-primary cursor-pointer"
        />
      )
  }
}

/** Colour swatch that opens the shared ColorPopover (grid + opacity). Stores rgba. */
function ColorField({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false)
  const { hex, op } = parseColor(value)
  return (
    <BitsPopover
      open={open}
      onOpenChange={setOpen}
      align="right"
      anchor={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Colour"
          className="h-7 w-10 rounded-md border border-border cursor-pointer"
          style={{ background: value }}
        />
      }
    >
      <ColorPopover
        curColor={hex}
        curOpacity={op}
        onColor={(c) => onChange(rgba(c, op))}
        onOpacity={(v) => onChange(rgba(hex, v))}
      />
    </BitsPopover>
  )
}

/** Thickness — the same WIDTHS + line-preview buttons as the drawing toolbar. */
function ThicknessField({ value, onChange }: { value: number; onChange: (w: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {WIDTHS.map((w) => (
        <button
          key={w}
          type="button"
          onClick={() => onChange(w)}
          aria-label={`Thickness ${w}`}
          className={cn(
            'inline-flex h-8 w-9 items-center justify-center rounded-md border cursor-pointer transition-colors',
            value === w ? 'border-primary bg-muted' : 'border-border hover:bg-muted/60',
          )}
        >
          <span className="block w-4 bg-foreground" style={{ height: w }} />
        </button>
      ))}
    </div>
  )
}

export function WorkbenchIndicatorSettings({
  chart,
  instance,
  def,
  onChange,
  onClose,
}: WorkbenchIndicatorSettingsProps) {
  // Snapshot the settings on mount so Cancel can revert (edits preview live).
  const originalRef = useRef<IndicatorSettings | null>(null)
  if (originalRef.current === null) {
    originalRef.current = JSON.parse(JSON.stringify(instance.settings)) as IndicatorSettings
  }

  const s = instance.settings
  const setInput = (key: string, v: number) => onChange({ inputs: { ...s.inputs, [key]: v } })
  // Input rows: our defs from the schema; a builtin v1 has `p1..pk` — as many as
  // this INSTANCE carries (klinecharts regenerates one line per param).
  const inputRows: Array<{ key: string; label: string; spec?: InputSpec; value: number }> = def.builtin
    ? Object.keys(s.inputs)
        .filter((k) => /^p\d+$/.test(k))
        .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))
        .map((key) => ({ key, label: def.inputs.find((i) => i.key === key)?.label ?? `Param ${key.slice(1)}`, value: s.inputs[key] }))
    : def.inputs.map((spec) => ({ key: spec.key, label: spec.label, spec, value: s.inputs[spec.key] ?? (spec.kind === 'bool' ? (spec.default ? 1 : 0) : spec.default) }))
  // Colour rows: one per colour slot (a histogram has up + down); a builtin keeps
  // one per live figure.
  const liveFigures = Math.max(1, getIndicator(chart, instance.paneId, instance.engineName)?.figures?.length || 1)
  const colorRows: string[] = def.builtin
    ? Array.from({ length: liveFigures }, (_, i) => (liveFigures > 1 ? `Line ${i + 1}` : 'Color'))
    : colorSlotsFor(def).map((c) => c.label)

  const handleCancel = () => {
    if (originalRef.current) onChange(originalRef.current)
    onClose()
  }
  const handleSave = () => onClose()

  // Dragged by the header, kept inside the window; reopens centred.
  const [offset, setOffset] = useState({ dx: 0, dy: 0 })
  const dragRef = useRef<{ x: number; y: number; dx: number; dy: number; maxX: number; maxY: number } | null>(null)
  const onDragStart = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    const box = e.currentTarget.closest<HTMLElement>('[data-slot="dialog-content"]')
    if (!box) return
    const rect = box.getBoundingClientRect()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      dx: offset.dx,
      dy: offset.dy,
      maxX: Math.max(0, (window.innerWidth - rect.width) / 2),
      maxY: Math.max(0, (window.innerHeight - rect.height) / 2),
    }
  }
  const onDragMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d) return
    const clamp = (v: number, m: number) => Math.max(-m, Math.min(m, v))
    setOffset({ dx: clamp(d.dx + e.clientX - d.x, d.maxX), dy: clamp(d.dy + e.clientY - d.y, d.maxY) })
  }
  const onDragEnd = () => {
    dragRef.current = null
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) handleCancel() // Esc / X behave like Cancel
      }}
    >
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayClassName="bg-transparent backdrop-blur-none"
        className="v1 bg-popover max-w-[420px] sm:max-w-[420px] w-[92vw] p-0 gap-0 overflow-hidden flex flex-col max-h-[85dvh] transition-none"
        style={{ left: `calc(50% + ${offset.dx}px)`, top: `calc(50% + ${offset.dy}px)` }}
      >
        <DialogHeader
          className="px-4 pt-4 pb-3 border-b border-border shrink-0 touch-none select-none cursor-move"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <DialogTitle className="text-section text-foreground">{def.label}</DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 flex-1 min-h-0 overflow-y-auto">
          {inputRows.map((row) => (
            <Row key={row.key} label={row.label}>
              {row.spec ? (
                <InputField spec={row.spec} value={row.value} onChange={(v) => setInput(row.key, v)} />
              ) : (
                <NumberField label={row.label} value={row.value} min={1} onChange={(v) => setInput(row.key, v)} />
              )}
            </Row>
          ))}

          {inputRows.length > 0 && <div className="my-2 h-px bg-border" />}

          {colorRows.map((label, i) => (
            <Row key={i} label={label}>
              <ColorField
                value={s.colors[i] ?? '#FF9600'}
                onChange={(c) => {
                  const colors = [...s.colors]
                  colors[i] = c
                  onChange({ colors })
                }}
              />
            </Row>
          ))}
          <Row label="Thickness">
            <ThicknessField value={s.thickness} onChange={(t) => onChange({ thickness: t })} />
          </Row>

          <div className="my-2 h-px bg-border" />

          {/* Text colour — the legend value + price-scale tag. Defaults to following
              the line colour; set it when a pale line reads poorly on a light theme. */}
          <Row label="Text colour">
            <ColorField
              value={s.textColor ?? s.colors[0] ?? '#FF9600'}
              onChange={(c) => onChange({ textColor: c })}
            />
          </Row>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
